/**
 * Firebase service for the Needs Map
 */

import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  DocumentData
} from 'firebase/firestore';
import { MapData } from '@/hooks/useMapData';
import { TopicNode } from '@/types/nodeTypes';
import { TopicEdge } from '@/types/edgeTypes';

/**
 * Firebase configuration
 * In a real implementation, these values would come from environment variables
 */
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID
};

/**
 * Initialize Firebase
 */
function initializeFirebase() {
  const apps = getApps();
  if (apps.length === 0) {
    return initializeApp(firebaseConfig);
  } else {
    return getApp();
  }
}

/**
 * Get Firestore instance
 */
export function getFirestoreInstance() {
  const app = initializeFirebase();
  return getFirestore(app);
}

/**
 * Convert MapData to Firestore document
 */
function mapDataToDocument(mapData: MapData): DocumentData {
  return {
    id: mapData.id,
    name: mapData.name,
    nodes: mapData.nodes,
    edges: mapData.edges,
    transcript: mapData.transcript,
    updatedAt: Timestamp.now()
  };
}

/**
 * Convert Firestore document to MapData
 */
function documentToMapData(doc: DocumentData): MapData {
  return {
    id: doc.id,
    name: doc.name,
    nodes: doc.nodes as TopicNode[],
    edges: doc.edges as TopicEdge[],
    transcript: doc.transcript
  };
}

/**
 * Firebase service
 */
export const firebaseService = {
  /**
   * Get a map by ID
   */
  async getMap(mapId: string): Promise<MapData | null> {
    try {
      const db = getFirestoreInstance();
      const mapDoc = await getDoc(doc(db, 'maps', mapId));
      
      if (mapDoc.exists()) {
        return documentToMapData(mapDoc.data());
      }
      
      return null;
    } catch (error) {
      console.error('Error getting map:', error);
      throw error;
    }
  },
  
  /**
   * Get all maps
   */
  async getMaps(): Promise<MapData[]> {
    try {
      const db = getFirestoreInstance();
      const mapsQuery = query(
        collection(db, 'maps'),
        orderBy('updatedAt', 'desc')
      );
      
      const mapsSnapshot = await getDocs(mapsQuery);
      const maps: MapData[] = [];
      
      mapsSnapshot.forEach(doc => {
        maps.push(documentToMapData(doc.data()));
      });
      
      return maps;
    } catch (error) {
      console.error('Error getting maps:', error);
      throw error;
    }
  },
  
  /**
   * Create a new map
   */
  async createMap(mapData: MapData): Promise<string> {
    try {
      const db = getFirestoreInstance();
      const mapDoc = doc(db, 'maps', mapData.id);
      
      await setDoc(mapDoc, mapDataToDocument(mapData));
      
      return mapData.id;
    } catch (error) {
      console.error('Error creating map:', error);
      throw error;
    }
  },
  
  /**
   * Update a map
   */
  async updateMap(mapData: MapData): Promise<void> {
    try {
      const db = getFirestoreInstance();
      const mapDoc = doc(db, 'maps', mapData.id);
      
      await updateDoc(mapDoc, mapDataToDocument(mapData));
    } catch (error) {
      console.error('Error updating map:', error);
      throw error;
    }
  },
  
  /**
   * Delete a map
   */
  async deleteMap(mapId: string): Promise<void> {
    try {
      const db = getFirestoreInstance();
      const mapDoc = doc(db, 'maps', mapId);
      
      await deleteDoc(mapDoc);
    } catch (error) {
      console.error('Error deleting map:', error);
      throw error;
    }
  },
  
  /**
   * Update a node
   */
  async updateNode(mapId: string, nodeId: string, updates: Partial<TopicNode>): Promise<void> {
    try {
      const db = getFirestoreInstance();
      const mapDoc = doc(db, 'maps', mapId);
      const mapData = await getDoc(mapDoc);
      
      if (mapData.exists()) {
        const data = mapData.data();
        const nodes = data.nodes as TopicNode[];
        
        const updatedNodes = nodes.map(node => 
          node.id === nodeId ? { ...node, ...updates } : node
        );
        
        await updateDoc(mapDoc, { nodes: updatedNodes, updatedAt: Timestamp.now() });
      }
    } catch (error) {
      console.error('Error updating node:', error);
      throw error;
    }
  },
  
  /**
   * Update an edge
   */
  async updateEdge(mapId: string, edgeId: string, updates: Partial<TopicEdge>): Promise<void> {
    try {
      const db = getFirestoreInstance();
      const mapDoc = doc(db, 'maps', mapId);
      const mapData = await getDoc(mapDoc);
      
      if (mapData.exists()) {
        const data = mapData.data();
        const edges = data.edges as TopicEdge[];
        
        const updatedEdges = edges.map(edge => 
          edge.id === edgeId ? { ...edge, ...updates } : edge
        );
        
        await updateDoc(mapDoc, { edges: updatedEdges, updatedAt: Timestamp.now() });
      }
    } catch (error) {
      console.error('Error updating edge:', error);
      throw error;
    }
  }
};
